import { compact } from 'lodash';
import { ReactElement, ReactNode, useMemo } from 'react';

import { Bean } from '@dreryk/react-common';
import {
   AlignItems,
   createDynamicColumnsWidth,
   EmptyStatePlaceholder,
   HorizontalAlign,
   InteractiveTable,
   InteractiveTableColumnConfiguration,
   JustifyContent,
   ValueRendererProps,
} from '@dreryk/react-components';

import { receptionServiceTranslationKeys } from '../../../../../../translations';
import { ReceptionServiceFormController } from '../../../ReceptionServiceForm';
import { receptionServicePriceVisitor } from '../../price/receptionServicePrice.utils';
import {
   ReceptionServicePractitionerIndividualPrice,
   ReceptionServicePractitionerIndividualPriceRange,
   ReceptionServicePractitionerValue,
} from '../receptionServicePractitioner.types';
import { receptionServicePractitionerPriceVisitor } from '../receptionServicePractitioner.utils';

import { ReceptionServiceEstimatedDurationCell } from './cells/ReceptionServiceEstimatedDurationCell';
import { ReceptionServicePractitionerDisplayNameCell } from './cells/ReceptionServicePractitionerDisplayNameCell';
import { ReceptionServicePractitionerIndividualPriceCell } from './cells/ReceptionServicePractitionerIndividualPriceCell';
import { ReceptionServicePractitionerIndividualPriceRangeCell } from './cells/ReceptionServicePractitionerIndividualPriceRangeCell';
import { ReceptionServicePractitionerPriceRangeCheckedCell } from './cells/ReceptionServicePractitionerPriceRangeCheckedCell';
import { ReceptionServicePractitionerRemoveConfirmationEventInterceptor } from './cells/ReceptionServicePractitionerRemoveConfirmationEventInterceptor';
import { ReceptionServicePractitionerSpecialtiesCell } from './cells/ReceptionServicePractitionerSpecialtiesCell';
import {
   ReceptionServicePractitionerUsingDefaultConfigurationCell,
   ReceptionServicePractitionerUsingDefaultConfigurationCellHeader,
} from './cells/ReceptionServicePractitionerUsingDefaultConfigurationCell';
import { ReceptionServicePractitionersInteractiveTableSelector } from './ReceptionServicePractitionersInteractiveTableSelector';
import { ReceptionServicePractitionersInteractiveTableSynchronization } from './ReceptionServicePractitionersInteractiveTableSynchronization';

type ReceptionServicePractitionersInteractiveTableColumnConfigurationOptions = Pick<
   ReceptionServiceFormController,
   'control'
> & {
   readOnly: boolean;
   receptionServiceName: string | undefined;
   removeWithConfirmation: boolean | undefined;
};

// eslint-disable-next-line max-lines-per-function
const useReceptionServicePractitionersInteractiveTableColumnConfigurationOptions = (
   options: ReceptionServicePractitionersInteractiveTableColumnConfigurationOptions
): InteractiveTableColumnConfiguration<ReceptionServicePractitionerValue>[] => {
   const { control, readOnly, removeWithConfirmation, receptionServiceName } = options;

   // eslint-disable-next-line max-lines-per-function
   return useMemo(() => {
      const columnWidths = createDynamicColumnsWidth({
         id: '0px',
         displayName: '50%',
         specialties: '50%',
         price: readOnly ? '0px' : '235px',
         priceRangeChecked: readOnly ? '0px' : '80px',
         usingDefaultConfiguration: '40px',
         estimatedDuration: '105px',
         options: '24px',
      });

      return compact([
         {
            name: 'id',
            width: columnWidths.id,
            renderer: ({ bean, index }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <InteractiveTable.ControllerCell testId={'id'} name={'id'} index={index} defaultValue={bean.id} />
               );
            },
            hidden: true,
         },
         {
            name: 'displayName',
            label:
               receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers.displayName,
            width: columnWidths.displayName,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            renderer: ({ bean, index }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <ReceptionServicePractitionerDisplayNameCell
                     testId={'displayName'}
                     name={'displayName'}
                     index={index}
                     defaultValue={bean.displayName}
                  />
               );
            },
         },
         {
            name: 'specialties',
            width: columnWidths.specialties,
            justifyContent: JustifyContent.START,
            alignItems: AlignItems.CENTER,
            label:
               receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers.specialties,
            renderer: ({ bean, index }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <ReceptionServicePractitionerSpecialtiesCell
                     testId={'specialties'}
                     name={'specialties'}
                     index={index}
                     defaultValue={bean.specialties}
                  />
               );
            },
         },
         !readOnly && {
            name: 'price',
            width: columnWidths.price,
            label: receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers.price,
            justifyContent: JustifyContent.END,
            textAlign: HorizontalAlign.RIGHT,
            renderer: ({ index, bean }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               const receptionService = control.getValues();
               const practitioner = receptionService.practitioners?.value?.at(index) ?? bean;

               const getIndividualPriceCell = (
                  price: ReceptionServicePractitionerIndividualPrice | null
               ): JSX.Element => (
                  <ReceptionServicePractitionerIndividualPriceCell
                     testId={'individualPrice'}
                     name={'individualPrice'}
                     defaultValue={price}
                     index={index}
                  />
               );

               const getIndividualPriceRangeCell = (
                  priceRange: ReceptionServicePractitionerIndividualPriceRange | null
               ): JSX.Element => (
                  <ReceptionServicePractitionerIndividualPriceRangeCell
                     testId={'individualPriceRange'}
                     name={'individualPriceRange'}
                     defaultValue={priceRange}
                     index={index}
                  />
               );

               return receptionServicePriceVisitor(receptionService)({
                  asFree: () => null,
                  withPrice: ({ price }) =>
                     receptionServicePractitionerPriceVisitor(practitioner)({
                        withDefaultConfiguration: () => getIndividualPriceCell(price),
                        withPrice: ({ individualPrice }) => getIndividualPriceCell(individualPrice),
                        withPriceRange: ({ individualPriceRange }) => getIndividualPriceRangeCell(individualPriceRange),
                     })!,
                  withPriceRange: ({ priceRange }) =>
                     receptionServicePractitionerPriceVisitor(practitioner)({
                        withDefaultConfiguration: () => getIndividualPriceRangeCell(priceRange),
                        withPrice: ({ individualPrice }) => getIndividualPriceCell(individualPrice),
                        withPriceRange: ({ individualPriceRange }) => getIndividualPriceRangeCell(individualPriceRange),
                     })!,
               })!;
            },
         },
         !readOnly && {
            name: 'priceRangeChecked',
            width: columnWidths.priceRangeChecked,
            label:
               receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers
                  .priceRangeChecked,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            renderer: ({ index, bean }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               const receptionService = control.getValues();
               const practitioner = receptionService.practitioners?.value?.at(index) ?? bean;

               const defaultValue = receptionServicePractitionerPriceVisitor(practitioner)({
                  withDefaultConfiguration: () => !receptionService.freeChecked && receptionService.priceRangeChecked,
                  withPrice: () => false,
                  withPriceRange: () => true,
               })!;

               return (
                  <ReceptionServicePractitionerPriceRangeCheckedCell
                     testId={'priceRangeChecked'}
                     name={'priceRangeChecked'}
                     index={index}
                     defaultValue={defaultValue}
                     control={control}
                  />
               );
            },
         },
         {
            name: 'estimatedDuration',
            width: columnWidths.estimatedDuration,
            label:
               receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.headers
                  .estimatedDuration,
            renderer: ({ bean, index }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <ReceptionServiceEstimatedDurationCell
                     defaultValue={bean.estimatedDuration}
                     index={index}
                     name={'estimatedDuration'}
                     testId={'estimatedDuration'}
                  />
               );
            },
         },
         {
            name: 'usingDefaultConfiguration',
            width: columnWidths.usingDefaultConfiguration,
            headerRenderer: ReceptionServicePractitionerUsingDefaultConfigurationCellHeader,
            justifyContent: JustifyContent.CENTER,
            textAlign: HorizontalAlign.CENTER,
            renderer: ({ index, bean }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <ReceptionServicePractitionerUsingDefaultConfigurationCell
                     testId={'usingDefaultConfiguration'}
                     name={'usingDefaultConfiguration'}
                     index={index}
                     defaultValue={bean.usingDefaultConfiguration}
                     control={control}
                  />
               );
            },
         },
         {
            name: 'options',
            width: columnWidths.options,
            renderer: ({ index, bean }: ValueRendererProps<ReceptionServicePractitionerValue>) => {
               return (
                  <ReceptionServicePractitionerRemoveConfirmationEventInterceptor
                     receptionServiceName={receptionServiceName}
                     practitionerName={bean.displayName}
                     shouldShowModal={removeWithConfirmation}
                  >
                     <InteractiveTable.DeleteRowCell index={index} />
                  </ReceptionServicePractitionerRemoveConfirmationEventInterceptor>
               );
            },
         },
      ]);
   }, [control, readOnly, receptionServiceName, removeWithConfirmation]);
};

type ReceptionServicePractitionersInteractiveTableProps = Pick<
   ReceptionServiceFormController,
   'register' | 'control'
> & {
   readonly header: ReactNode;
   readonly bottomElement: ReactElement;
   readonly readOnly: boolean;
   readonly receptionServiceName?: string;
   readonly removeWithConfirmation?: boolean;
};

export const ReceptionServicePractitionersInteractiveTable = (
   props: ReceptionServicePractitionersInteractiveTableProps
): JSX.Element => {
   const columns = useReceptionServicePractitionersInteractiveTableColumnConfigurationOptions({
      control: props.control,
      readOnly: props.readOnly,
      receptionServiceName: props.receptionServiceName,
      removeWithConfirmation: props.removeWithConfirmation,
   });

   return (
      <InteractiveTable<ReceptionServicePractitionerValue, Bean>
         name={'practitioners.value'}
         register={props.register}
         columns={columns}
         testId={'practitioners'}
         header={props.header}
         placeholder={
            <EmptyStatePlaceholder
               heading={
                  receptionServiceTranslationKeys.shared.form.components.practitionersInteractiveTable.placeholder
                     .heading
               }
               testId={'noPractitioners'}
            />
         }
         bottomElement={props.bottomElement}
      />
   );
};

ReceptionServicePractitionersInteractiveTable.Selector = ReceptionServicePractitionersInteractiveTableSelector;
ReceptionServicePractitionersInteractiveTable.Synchronization = ReceptionServicePractitionersInteractiveTableSynchronization;
